import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, Loader2, CheckCircle2, ArrowLeft, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/constants";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ops-command-center-87/artifacts/fkrzr67n_4446800E-690F-4C11-82C8-015EFBEC0CAD.png";

export default function FieldExpensePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const [bill, setBill] = useState(null); // {id, filename, preview_url}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    person_name: user?.name || "",
    date: new Date().toISOString().slice(0, 10),
    visit_location: "",
    purpose: "",
    customer_department: "",
    travel_expense: "",
    food_expense: "",
    stay_expense: "",
    local_conveyance: "",
    other_expense: "",
  });
  const [recent, setRecent] = useState([]);

  const loadRecent = () => {
    api.get("/modules/engineer_expense/records?limit=10").then((r) => setRecent(r.data.items || [])).catch(() => {});
  };
  useEffect(() => { loadRecent(); }, []);

  const total = ["travel_expense", "food_expense", "stay_expense", "local_conveyance", "other_expense"]
    .reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const preview_url = URL.createObjectURL(file);
      // Cookie-based auth (same-origin /api) — no JWT in the URL.
      setBill({ ...data, preview_url, download_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.id}` });
      toast.success("Bill photo attached");
    } catch (err) {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeBill = () => {
    if (bill?.preview_url) URL.revokeObjectURL(bill.preview_url);
    setBill(null);
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const submit = async () => {
    if (!form.person_name || !form.visit_location) {
      toast.error("Person name and visit location are required");
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form,
        travel_expense: Number(form.travel_expense) || 0,
        food_expense: Number(form.food_expense) || 0,
        stay_expense: Number(form.stay_expense) || 0,
        local_conveyance: Number(form.local_conveyance) || 0,
        other_expense: Number(form.other_expense) || 0,
        total: total,
        status: "Pending",
        attachments: bill ? [{ id: bill.id, filename: bill.filename, size: bill.size, content_type: bill.content_type }] : [],
      };
      await api.post("/modules/engineer_expense/records", { data });
      toast.success("Expense submitted for approval");
      setForm({
        person_name: user?.name || "",
        date: new Date().toISOString().slice(0, 10),
        visit_location: "",
        purpose: "",
        customer_department: "",
        travel_expense: "",
        food_expense: "",
        stay_expense: "",
        local_conveyance: "",
        other_expense: "",
      });
      removeBill();
      loadRecent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="field-expense-page">
      {/* Mobile header */}
      <header className="sticky top-0 z-20 h-14 bg-slate-900 text-white flex items-center px-4 shadow">
        <button
          onClick={() => navigate("/")}
          className="p-2 -ml-2 rounded hover:bg-slate-800"
          data-testid="field-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <img src={LOGO_URL} alt="HM" className="h-7 w-7 bg-white rounded p-0.5 ml-2" />
        <div className="ml-2 leading-tight">
          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Field Console</div>
          <div className="text-sm font-bold">Capture Expense</div>
        </div>
      </header>

      <main className="p-4 space-y-4 max-w-lg mx-auto pb-24">
        {/* Camera capture card */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Bill / Receipt</div>

            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCapture}
              className="hidden"
              data-testid="field-camera-input"
            />

            {!bill ? (
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                className="w-full aspect-[4/3] rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white flex flex-col items-center justify-center gap-3 active:scale-[0.98] transition-transform"
                data-testid="field-capture-button"
              >
                {uploading ? (
                  <Loader2 className="h-10 w-10 animate-spin" />
                ) : (
                  <>
                    <Camera className="h-12 w-12" strokeWidth={1.5} />
                    <div className="text-sm font-semibold">Tap to capture bill</div>
                    <div className="text-xs text-slate-300">Uses back camera on mobile</div>
                  </>
                )}
              </button>
            ) : (
              <div className="relative">
                <img
                  src={bill.preview_url}
                  alt="Bill preview"
                  className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200"
                />
                <button
                  onClick={removeBill}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-slate-900/80 text-white flex items-center justify-center"
                  data-testid="field-remove-bill"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Attached
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form */}
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <FieldRow label="Person" testid="field-person">
              <Input value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} data-testid="field-person-input" />
            </FieldRow>
            <FieldRow label="Date" testid="field-date">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="field-date-input" />
            </FieldRow>
            <FieldRow label="Visit Location *" testid="field-location">
              <Input value={form.visit_location} onChange={(e) => setForm({ ...form, visit_location: e.target.value })} placeholder="e.g., AIIMS Delhi" data-testid="field-location-input" />
            </FieldRow>
            <FieldRow label="Customer / Department" testid="field-customer">
              <Input value={form.customer_department} onChange={(e) => setForm({ ...form, customer_department: e.target.value })} data-testid="field-customer-input" />
            </FieldRow>
            <FieldRow label="Purpose" testid="field-purpose">
              <Textarea rows={2} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="field-purpose-input" />
            </FieldRow>

            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Expenses</div>
              <div className="grid grid-cols-2 gap-2.5">
                <ExpenseInput label="Travel" value={form.travel_expense} onChange={(v) => setForm({ ...form, travel_expense: v })} testid="field-travel" />
                <ExpenseInput label="Food" value={form.food_expense} onChange={(v) => setForm({ ...form, food_expense: v })} testid="field-food" />
                <ExpenseInput label="Stay" value={form.stay_expense} onChange={(v) => setForm({ ...form, stay_expense: v })} testid="field-stay" />
                <ExpenseInput label="Local" value={form.local_conveyance} onChange={(v) => setForm({ ...form, local_conveyance: v })} testid="field-local" />
                <ExpenseInput label="Other" value={form.other_expense} onChange={(v) => setForm({ ...form, other_expense: v })} testid="field-other" />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between p-3 rounded-md bg-slate-900 text-white">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Total</div>
              <div className="text-xl font-bold" data-testid="field-total">{formatCurrency(total)}</div>
            </div>
          </CardContent>
        </Card>

        {/* Recent */}
        {recent.length > 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Recent Entries</div>
              <div className="space-y-2">
                {recent.slice(0, 5).map((r) => (
                  <div key={r.id} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50">
                    <div className="min-w-0">
                      <div className="text-slate-900 font-medium truncate">{r.data?.visit_location || r.data?.person_name}</div>
                      <div className="text-[11px] text-slate-500">{r.data?.date} · {r.data?.status || "—"}</div>
                    </div>
                    <div className="text-sm font-semibold font-mono text-slate-900">{formatCurrency(r.data?.total || 0)}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Sticky bottom submit */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-30">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={submit}
            disabled={saving || !form.visit_location}
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-base font-semibold"
            data-testid="field-submit-button"
          >
            {saving ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
            Submit Expense · {formatCurrency(total)}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, testid, children }) {
  return (
    <div>
      <Label className="text-slate-700 text-[11px] font-semibold uppercase tracking-wider" data-testid={`${testid}-label`}>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ExpenseInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-slate-700 text-[11px] font-semibold uppercase tracking-wider">{label}</Label>
      <div className="relative mt-1">
        <span className="absolute left-3 top-2.5 text-slate-400 text-sm">₹</span>
        <Input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pl-7"
          placeholder="0"
          data-testid={`${testid}-input`}
        />
      </div>
    </div>
  );
}
